/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function setupCardImage(image, source, width = 240) {
  let optimizedSource = source;
  try {
    const url = new URL(source, window.location.origin);
    if (url.hostname.includes('shopify')) {
      url.searchParams.set('width', String(width));
      optimizedSource = url.toString();
    }
  } catch (error) {
    // Keep the original source when a third-party URL cannot be normalized.
  }
  image.src = optimizedSource;
  image.alt = '';
  image.loading = 'lazy';
  image.decoding = 'async';
  image.width = width;
  image.height = width;
}

class AirManagementSetup extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-ams-data]').textContent);
    this.state = { skipped: false, path: null, series: null, collection: null, product: null, options: [] };
    this.addEventListener('change', (event) => this.onChange(event));
    this.render();
  }
  panel(name) { return this.querySelector(`[data-ams-panel="${name}"]`); }
  resetFrom(key) {
    const keys = ['path', 'series', 'collection', 'product', 'options'];
    keys.slice(keys.indexOf(key)).forEach((name) => { this.state[name] = name === 'options' ? [] : null; });
  }
  uncheck(prefixes) {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (prefixes.some((prefix) => input.name.startsWith(prefix))) input.checked = false;
    });
  }
  onChange(event) {
    const input = event.target;
    if (input.name?.startsWith('ams-path-')) {
      this.resetFrom('series'); this.state.path = input.value; this.state.skipped = input.value === 'none'; this.state.collection = input.value === 'air-bar' ? this.data.airBarCollection : input.value === 'customize' ? this.data.customizeCollection : null; this.uncheck(['ams-series-', 'ams-product-']);
    } else if (input.name?.startsWith('ams-series-')) {
      this.resetFrom('collection'); this.state.series = input.dataset.seriesName || input.dataset.collectionHandle || input.value; this.state.collection = input.dataset.collectionHandle; this.uncheck(['ams-product-']);
    } else if (input.name?.startsWith('ams-product-')) {
      this.state.product = this.products().find((product) => String(product.id) === input.value) || null; this.state.options = [];
    } else if (input.matches('[data-ams-option]')) {
      const optionIndex = Number(input.dataset.amsOption); this.state.options[optionIndex] = input.value; this.state.options = this.state.options.slice(0, optionIndex + 1);
    }
    this.render();
  }
  products() { return this.data.collections[this.state.collection]?.products || []; }
  money(cents) {
    if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, this.data.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }
  render() {
    const active = !this.state.skipped;
    this.dataset.path = this.state.path || '';
    // Even the "No" path writes order properties to the base product, so it
    // must use the bundle-aware cart submission instead of Shopify's native
    // dynamic checkout button.
    this.dataset.requiresCartSubmission = 'true';
    syncSetupDynamicCheckout(this.getAttribute('form-id'));
    this.panel('paths').hidden = false;
    this.panel('series').hidden = !(active && this.state.path === 'standard');
    this.querySelector('[data-ams-empty-series]').hidden = Boolean(this.querySelector('[name^="ams-series-"]'));
    this.panel('products').hidden = !(active && this.state.collection);
    if (active && this.state.collection) {
      const availableProducts = this.products().filter((product) => product.variants.length);
      if (!availableProducts.includes(this.state.product)) { this.state.product = availableProducts[0] || null; this.state.options = []; }
      this.querySelector('[data-ams-products-heading]').textContent = this.state.path === 'air-bar' ? this.data.airBarProductsHeading : this.state.path === 'standard' ? this.data.standardProductsHeading : this.data.customizeProductsHeading;
      this.renderProducts();
    }
    this.panel('options').hidden = !this.state.product;
    if (this.state.product) this.renderOptions();
  }
  renderProducts() {
    const container = this.querySelector('[data-ams-products]');
    const message = this.querySelector('[data-ams-products-message]');
    const products = this.products().filter((product) => product.variants.length);
    container.replaceChildren(); container.style.setProperty('--ams-grid-columns', Math.min(products.length || 1, 4)); message.hidden = Boolean(products.length);
    if (!products.length) { message.textContent = this.products().length ? 'No products in this collection have an available variant.' : 'This collection is empty or has not been configured.'; return; }
    products.forEach((product) => {
      const label = document.createElement('label'); label.className = 'air-management-setup__product';
      const input = document.createElement('input'); input.type = 'radio'; input.name = `ams-product-${this.getAttribute('form-id')}`; input.value = product.id; input.checked = this.state.product?.id === product.id;
      const card = document.createElement('span');
      if (product.image) { const image = document.createElement('img'); setupCardImage(image, product.image); card.append(image); }
      const copy = document.createElement('span'); copy.className = 'air-management-setup__product-copy';
      const badges = document.createElement('span'); badges.className = 'air-management-setup__badges';
      const badgeTypes = [['new', 'new'], ['hot', 'hot'], ['bestSelling', 'best-selling'], ['airPressure', 'air-pressure'], ['heightSensor', 'height-sensor']];
      if (this.state.path !== 'customize') badgeTypes.forEach(([key, className]) => {
        const badgeLabel = String(this.data.badgeLabels?.[key] || '').trim();
        if (product.features?.[key] && badgeLabel) {
          const badge = document.createElement('small'); badge.className = `air-management-setup__badge air-management-setup__badge--${className}`; badge.textContent = badgeLabel; badges.append(badge);
        }
      });
      const title = document.createElement('strong'); title.textContent = product.title;
      const displayVariant = product.variants.reduce((lowest, variant) => variant.price < lowest.price ? variant : lowest);
      const onSale = displayVariant.compareAtPrice > displayVariant.price;
      label.classList.toggle('air-management-setup__product--on-sale', onSale);
      const prices = document.createElement('span'); prices.className = 'air-management-setup__prices';
      const price = document.createElement('span'); price.className = 'air-management-setup__price'; price.textContent = this.money(displayVariant.price); prices.append(price);
      if (onSale) { const comparePrice = document.createElement('s'); comparePrice.className = 'air-management-setup__compare-price'; comparePrice.textContent = this.money(displayVariant.compareAtPrice); prices.append(comparePrice); }
      if (badges.childElementCount) copy.append(badges); copy.append(title, prices);
      card.append(copy); label.append(input, card); container.append(label);
    });
  }
  renderOptions() {
    const container = this.querySelector('[data-ams-options]'); container.replaceChildren(); container.className = 'air-management-setup__group air-management-setup__options';
    this.state.product.options.forEach((option, index) => {
      const group = document.createElement('fieldset'); group.className = 'air-management-setup__option-group';
      const title = document.createElement('legend'); title.textContent = option.name; group.append(title);
      const values = document.createElement('div'); values.className = 'air-management-setup__option-values'; values.style.setProperty('--ams-grid-columns', Math.min(option.values.length || 1, 4));
      option.values.forEach((value) => {
        const label = document.createElement('label');
        const input = document.createElement('input'); input.type = 'radio'; input.name = `ams-option-${this.getAttribute('form-id')}-${index}`; input.value = value; input.dataset.amsOption = index; input.checked = this.state.options[index] === value; input.disabled = !this.managementOptionValueAvailable(index, value);
        const text = document.createElement('span'); const name = document.createElement('strong'); name.textContent = value; const priceValue = this.managementOptionValuePrice(index, value); const price = document.createElement('small'); price.textContent = priceValue ? this.money(priceValue) : 'Unavailable'; text.append(name, price);
        label.append(input, text); values.append(label);
      });
      group.append(values); container.append(group);
    });
    const variant = this.selectedVariant();
    const message = this.querySelector('[data-ams-variant-message]'); message.dataset.state = variant ? 'success' : 'info'; message.textContent = this.state.options.length < this.state.product.options.length || this.state.options.some((value) => !value) ? 'Select every option to complete this setup.' : variant ? `${variant.title} — ${this.money(variant.price)}${variant.compareAtPrice > variant.price ? ` (was ${this.money(variant.compareAtPrice)})` : ''}` : 'This option combination is unavailable. Please choose another combination.';
  }
  managementOptionValuePrice(optionIndex, value) {
    const variants = this.state.product.variants.filter((variant) => variant.options.every((variantValue, index) => index === optionIndex ? variantValue === value : !this.state.options[index] || variantValue === this.state.options[index]));
    return variants.length ? Math.min(...variants.map((variant) => variant.price)) : 0;
  }
  managementOptionValueAvailable(optionIndex, value) {
    return this.state.product.variants.some((variant) => variant.options.every((variantValue, index) => index === optionIndex ? variantValue === value : !this.state.options[index] || variantValue === this.state.options[index]));
  }
  selectedVariant() {
    if (!this.state.product || this.state.options.length !== this.state.product.options.length || this.state.options.some((value) => !value)) return null;
    return this.state.product.variants.find((variant) => variant.options.every((value, index) => value === this.state.options[index])) || null;
  }
  reportProviderError(message) { const target = this.querySelector('[data-ams-variant-message]'); this.panel('options').hidden = false; target.dataset.state = 'error'; target.textContent = message; this.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  getCartItem(bundleId) {
    if (this.state.skipped) return { mainProperties: { 'Air Management': this.data.noName || 'No' } };
    if (!this.state.path) return { error: 'Choose an Air Management type before adding to cart.' };
    if (this.state.path === 'standard' && !this.state.series) return { error: 'Choose a Standard category before adding to cart.' };
    if (!this.state.product) return { error: 'Choose an Air Management product before adding to cart.' };
    const variant = this.selectedVariant(); if (!variant) return { error: 'Choose a complete, available option combination before adding to cart.' };
    const pathName = this.state.path === 'air-bar' ? this.data.airBarName : this.state.path === 'standard' ? this.data.standardName : this.data.customizeName;
    const properties = { '_Bundle component': 'Air Management', 'Control Path': pathName, 'Air Management System': this.state.product.title, '_Bundle ID': bundleId };
    if (this.state.series) properties['Standard Category'] = this.state.series;
    this.state.product.options.forEach((option, index) => { properties[option.name] = this.state.options[index]; });
    const mainProperties = { 'Air Management - Control Path': pathName, 'Air Management - System': this.state.product.title };
    if (this.state.series) mainProperties['Air Management - Standard Category'] = this.state.series;
    this.state.product.options.forEach((option, index) => { mainProperties[`Air Management - ${option.name}`] = this.state.options[index]; });
    return { id: variant.id, quantity: 1, properties, mainProperties };
  }
}
if (!customElements.get('air-management-setup')) customElements.define('air-management-setup', AirManagementSetup);

class AirManagementConfigurator extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-amc-data]').textContent);
    this.state = { need: this.data.defaultNeed ? 'yes' : 'no', path: null, pathName: null, collection: null, product: null, options: [] };
    this.addEventListener('change', (event) => this.onChange(event)); this.render();
  }
  panel(name) { return this.querySelector(`[data-amc-panel="${name}"]`); }
  onChange(event) {
    const input = event.target;
    if (input.name?.startsWith('amc-need-')) { this.state.need = input.value; this.resetPath(); }
    else if (input.name?.startsWith('amc-path-')) { this.state.path = input.value; this.state.pathName = input.dataset.pathName; this.state.collection = input.dataset.collectionHandle; this.state.product = null; this.state.options = []; }
    else if (input.name?.startsWith('amc-product-')) { this.state.product = this.products().find((product) => String(product.id) === input.value) || null; this.state.options = []; }
    else if (input.matches('[data-amc-option]')) { const index = Number(input.dataset.amcOption); this.state.options[index] = input.value; this.state.options = this.state.options.slice(0, index + 1); }
    this.render();
  }
  resetPath() { this.state.path = null; this.state.pathName = null; this.state.collection = null; this.state.product = null; this.state.options = []; this.querySelectorAll('input[name^="amc-path-"]').forEach((input) => { input.checked = false; }); }
  products() { return this.data.collections[this.state.collection]?.products || []; }
  money(cents) { if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, this.data.moneyFormat); return new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:window.Shopify?.currency?.active || 'USD' }).format(cents / 100); }
  render() {
    const active = this.state.need === 'yes'; this.dataset.requiresCartSubmission = active ? 'true' : 'false'; syncSetupDynamicCheckout(this.getAttribute('form-id'));
    this.panel('configuration').hidden = !active; this.panel('products').hidden = !(active && this.state.path); this.panel('options').hidden = !this.state.product;
    if (active && this.state.path) this.renderProducts(); if (this.state.product) this.renderOptions();
  }
  renderProducts() {
    const container = this.querySelector('[data-amc-products]'); const message = this.querySelector('[data-amc-products-message]'); const products = this.products(); container.replaceChildren(); container.style.setProperty('--ams-grid-columns', Math.min(products.length || 1, 4));
    message.hidden = products.length > 0; if (!products.length) { message.textContent = 'This collection is empty or has not been configured.'; this.state.product = null; return; }
    products.forEach((product) => {
      const available = product.variants.length > 0; const label = document.createElement('label'); label.className = `air-management-setup__product${available ? '' : ' air-management-setup__product--sold-out'}`;
      const input = document.createElement('input'); input.type='radio'; input.name=`amc-product-${this.getAttribute('form-id')}`; input.value=product.id; input.checked=this.state.product?.id===product.id; input.disabled=!available;
      const card=document.createElement('span'); if(product.image){const image=document.createElement('img');setupCardImage(image,product.image);card.append(image);}
      const copy=document.createElement('span');copy.className='air-management-setup__product-copy';const badges=document.createElement('span');badges.className='air-management-setup__badges';const availabilityText=String(available?this.data.availableLabel||'':'SOLD OUT').trim();if(availabilityText){const availability=document.createElement('small');availability.className='air-management-setup__badge';availability.textContent=availabilityText;badges.append(availability);}
      const title=document.createElement('strong');title.textContent=product.title;if(badges.childElementCount)copy.append(badges);copy.append(title);
      if(available){const displayVariant=product.variants.reduce((lowest,variant)=>variant.price<lowest.price?variant:lowest);const prices=document.createElement('span');prices.className='air-management-setup__prices';const price=document.createElement('span');price.className='air-management-setup__price';price.textContent=this.money(displayVariant.price);prices.append(price);copy.append(prices);}
      card.append(copy);label.append(input,card);container.append(label);
    });
  }
  renderOptions() {
    const container=this.querySelector('[data-amc-options]');container.replaceChildren();this.state.product.options.forEach((option,index)=>{const group=document.createElement('fieldset');group.className='air-management-setup__option-group';const legend=document.createElement('legend');legend.textContent=option.name;group.append(legend);const values=document.createElement('div');values.className='air-management-setup__option-values';values.style.setProperty('--ams-grid-columns',Math.min(option.values.length||1,4));option.values.forEach((value)=>{const label=document.createElement('label');const input=document.createElement('input');input.type='radio';input.name=`amc-option-${this.getAttribute('form-id')}-${index}`;input.value=value;input.dataset.amcOption=index;input.checked=this.state.options[index]===value;input.disabled=!this.optionAvailable(index,value);const box=document.createElement('span');const name=document.createElement('strong');name.textContent=value;const priceValue=this.optionPrice(index,value);const price=document.createElement('small');price.textContent=priceValue?this.money(priceValue):'Unavailable';box.append(name,price);label.append(input,box);values.append(label);});group.append(values);container.append(group);});
    const variant=this.selectedVariant();const message=this.querySelector('[data-amc-variant-message]');message.dataset.state=variant?'success':'info';message.textContent=this.state.options.length<this.state.product.options.length||this.state.options.some((value)=>!value)?'Select every option to complete this setup.':variant?`${variant.title} — ${this.money(variant.price)}`:'This option combination is unavailable.';
  }
  optionVariants(index,value){return this.state.product.variants.filter((variant)=>variant.options.every((variantValue,position)=>position===index?variantValue===value:!this.state.options[position]||variantValue===this.state.options[position]));}
  optionAvailable(index,value){return this.optionVariants(index,value).length>0;}
  optionPrice(index,value){const variants=this.optionVariants(index,value);return variants.length?Math.min(...variants.map((variant)=>variant.price)):0;}
  selectedVariant(){if(!this.state.product||this.state.options.length!==this.state.product.options.length||this.state.options.some((value)=>!value))return null;return this.state.product.variants.find((variant)=>variant.options.every((value,index)=>value===this.state.options[index]))||null;}
  reportProviderError(message){const target=this.querySelector('[data-amc-variant-message]');this.panel('options').hidden=false;target.dataset.state='error';target.textContent=message;this.scrollIntoView({behavior:'smooth',block:'center'});}
  getCartItem(bundleId){if(this.state.need!=='yes')return null;if(!this.state.path)return{error:'Choose an Air Management path before adding to cart.'};if(!this.state.product)return{error:'Choose an Air Management product before adding to cart.'};const variant=this.selectedVariant();if(!variant)return{error:'Choose a complete, available Air Management option combination.'};const properties={'_Bundle component':'Air Management','Control Path':this.state.pathName,'Air Management System':this.state.product.title,'_Bundle ID':bundleId};this.state.product.options.forEach((option,index)=>{properties[option.name]=this.state.options[index];});return{id:variant.id,quantity:1,properties};}
}
if(!customElements.get('air-management-configurator'))customElements.define('air-management-configurator',AirManagementConfigurator);

class AirSuspensionSetup extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.events = new AbortController();
    this.dataReady = false;
    this.loadFailed = false;
    this.data = JSON.parse(this.querySelector('[data-ass-data]').textContent);
    this.filters = ['make', 'model', 'year', 'drivetrain'];
    this.state = { make: '', model: '', year: '', drivetrain: '', product: null, variant: null, optionValues: [], skipped: false };
    this.products = [];
    this.addEventListener('change', (event) => this.onChange(event), { signal: this.events.signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal: this.events.signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.events.signal });
    this.outsideClickHandler = (event) => {
      if (!this.contains(event.target)) {
        this.closeFilters();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);
    this.populateFilter('make');
    this.render();
    this.loadFitment();
  }
  disconnectedCallback() { this.connected = false; this.events?.abort(); this.loadController?.abort(); document.removeEventListener('click', this.outsideClickHandler); this.unlockPageScroll(); }
  async fetchFitmentPage(page, pageParam, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) controller.abort();
    const timeout = window.setTimeout(abort, 15000);
    try {
      const url = new URL(this.data.collectionUrl, window.location.origin);
      url.searchParams.set('section_id', 'maxload-fitment-data');
      url.searchParams.set(pageParam, String(page));
      const response = await fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Fitment request failed');
      const root = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = root.querySelector('[data-maxload-fitment-page]');
      if (!script) throw new Error('Fitment data missing');
      const data = JSON.parse(script.textContent);
      if (String(data.collectionId) !== String(this.data.collectionId) || data.page !== page || !Number.isInteger(data.pages) || data.pages < 0 || !Number.isInteger(data.total) || data.total < 0 || !Array.isArray(data.products)) throw new Error('Invalid fitment page');
      return data;
    } finally {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    }
  }
  async loadFitment() {
    this.loadController?.abort();
    const controller = new AbortController();
    this.loadController = controller;
    this.dataReady = false;
    this.loadFailed = false;
    this.querySelector('[data-ass-retry]').hidden = true;
    this.products = [];
    this.closeFilters();
    this.resetFilters();
    this.render();
    try {
      let products = [];
      if (this.data.collectionUrl && this.data.collectionId) {
        const first = await this.fetchFitmentPage(1, 'page', controller.signal);
        products = [...first.products];
        for (let page = 2; page <= first.pages; page += 4) {
          const requests = [];
          for (let current = page; current < page + 4 && current <= first.pages; current += 1) requests.push(this.fetchFitmentPage(current, first.pageParam || 'page', controller.signal));
          const pages = await Promise.all(requests);
          pages.forEach(data => {
            if (data.total !== first.total || data.pages !== first.pages) throw new Error('Fitment collection changed');
            products.push(...data.products);
          });
        }
        const unique = new Map();
        products.forEach(product => {
          if (!product.id || !Array.isArray(product.variants) || !Array.isArray(product.options)) throw new Error('Invalid fitment product');
          unique.set(String(product.id), product);
        });
        if (unique.size !== first.total) throw new Error('Incomplete fitment collection');
        products = Array.from(unique.values());
      }
      if (controller.signal.aborted || this.loadController !== controller) return;
      this.products = products.map((product, index) => ({ ...product, index, fitment: this.productFitment(product) }));
      this.dataReady = true;
      this.populateFilter('make');
    } catch (error) {
      if (controller.signal.aborted || this.loadController !== controller) return;
      controller.abort();
      this.loadFailed = true;
      this.querySelector('[data-ass-retry]').hidden = false;
    }
    if (this.connected && this.loadController === controller) this.render();
  }
  panel(name) { return this.querySelector(`[data-ass-panel="${name}"]`); }
  normalizeFitmentValues(value) {
    const values = Array.isArray(value) ? value : value == null ? [] : String(value).split(/[,\n|]+/);
    return [...new Set(values.map((item) => String(item).trim()).filter(Boolean))];
  }
  productFitment(product) {
    return this.filters.reduce((fitment, key) => {
      fitment[key] = this.normalizeFitmentValues(product.fitment?.[key]);
      return fitment;
    }, {});
  }
  onChange(event) {
    const input = event.target;
    if (input.name?.startsWith('ass-product-')) {
      this.state.product = this.products.find((product) => product.index === Number(input.value)) || null;
      this.state.variant = null; this.state.optionValues = []; this.render(); return;
    }
    if (input.name?.startsWith('ass-option-')) {
      const optionIndex = Number(input.dataset.assOptionIndex); this.state.optionValues[optionIndex] = input.value; this.state.optionValues = this.state.optionValues.slice(0, optionIndex + 1);
      this.state.variant = this.selectedSuspensionVariant(); this.renderVariants(); this.renderStatus();
    }
  }
  onClick(event) {
    if (event.target.closest('[data-ass-retry]')) { this.loadFitment(); return; }
    if (event.target.closest('[data-ass-filter-close]')) { this.closeFilters(); return; }
    const skipButton = event.target.closest('[data-ass-skip]');
    if (skipButton) {
      this.state.skipped = !this.state.skipped;
      if (this.state.skipped) {
        this.filters.forEach((key) => { this.state[key] = ''; this.resetFilter(key); });
        this.populateFilter('make'); this.state.product = null; this.state.variant = null; this.state.optionValues = [];
      } else this.resetFilters();
      this.render(); return;
    }
    const trigger = event.target.closest('[data-ass-filter-trigger]');
    if (trigger && !trigger.disabled) {
      if (this.state.skipped) { this.state.skipped = false; this.render(); }
      this.toggleFilter(trigger.dataset.assFilterTrigger); return;
    }
    const option = event.target.closest('[data-ass-filter-option]');
    if (option) { this.selectFilter(option.dataset.assFilterOption, option.dataset.value); return; }
  }
  onKeydown(event) {
    const trigger = event.target.closest('[data-ass-filter-trigger]');
    if (trigger && ['ArrowDown', 'Enter', ' '].includes(event.key)) { event.preventDefault(); this.openFilter(trigger.dataset.assFilterTrigger, true); }
    if (event.key === 'Escape') { const openTrigger = this.querySelector('[data-ass-filter-trigger][aria-expanded="true"]'); this.closeFilters(); openTrigger?.focus(); }
  }
  toggleFilter(key) { const trigger = this.querySelector(`[data-ass-filter-trigger="${key}"]`); trigger.getAttribute('aria-expanded') === 'true' ? this.closeFilters() : this.openFilter(key); }
  openFilter(key, focusFirst = false) {
    this.closeFilters(); const trigger = this.querySelector(`[data-ass-filter-trigger="${key}"]`); const popover = this.querySelector(`[data-ass-filter-popover="${key}"]`);
    if (trigger.disabled) return; trigger.setAttribute('aria-expanded', 'true'); popover.hidden = false;
    if (window.matchMedia('(max-width: 740px)').matches) this.lockPageScroll();
    if (focusFirst) popover.querySelector('[data-ass-filter-option]')?.focus();
  }
  lockPageScroll() {
    if (document.documentElement.classList.contains('ass-filter-open')) return;
    const scrollY = window.scrollY;
    document.documentElement.dataset.assScrollY = String(scrollY);
    document.documentElement.classList.add('ass-filter-open');
    document.body.style.top = `-${scrollY}px`;
  }
  unlockPageScroll() {
    if (!document.documentElement.classList.contains('ass-filter-open')) return;
    const scrollY = Number(document.documentElement.dataset.assScrollY || 0);
    document.documentElement.classList.remove('ass-filter-open');
    delete document.documentElement.dataset.assScrollY;
    document.body.style.removeProperty('top');
    window.scrollTo(0, scrollY);
  }
  closeFilters() { this.querySelectorAll('[data-ass-filter-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false')); this.querySelectorAll('[data-ass-filter-popover]').forEach((popover) => { popover.hidden = true; }); this.unlockPageScroll(); }
  selectFilter(key, value) {
    this.state.skipped = false;
    const index = this.filters.indexOf(key); this.state[key] = value; this.updateFilterTrigger(key);
    this.filters.slice(index + 1).forEach((downstream) => { this.state[downstream] = ''; this.resetFilter(downstream); });
    this.state.product = null; this.state.variant = null; this.state.optionValues = []; const next = this.filters[index + 1]; if (next) this.populateFilter(next);
    this.closeFilters(); this.render();
    if (next) {
      const nextTrigger = this.querySelector(`[data-ass-filter-trigger="${next}"]`);
      if (nextTrigger && !nextTrigger.disabled) requestAnimationFrame(() => this.openFilter(next));
    }
  }
  resetFilters() {
    this.filters.forEach((key) => { this.state[key] = ''; this.resetFilter(key); }); this.populateFilter('make');
    this.state.product = null; this.state.variant = null; this.state.optionValues = [];
  }
  cancelSkip() {
    if (!this.state.skipped) return;
    this.state.skipped = false;
    this.resetFilters();
    this.closeFilters();
    this.render();
  }
  resetFilter(key) {
    const trigger = this.querySelector(`[data-ass-filter-trigger="${key}"]`); const options = this.querySelector(`[data-ass-filter-options="${key}"]`);
    trigger.disabled = !this.dataReady || key !== 'make'; trigger.dataset.hasValue = 'false'; trigger.querySelector('[data-ass-filter-value]').textContent = `Select ${key.charAt(0).toUpperCase() + key.slice(1)}`; options.replaceChildren();
  }
  updateFilterTrigger(key) {
    const trigger = this.querySelector(`[data-ass-filter-trigger="${key}"]`); trigger.querySelector('[data-ass-filter-value]').textContent = this.state[key] || `Select ${key.charAt(0).toUpperCase() + key.slice(1)}`; trigger.dataset.hasValue = String(Boolean(this.state[key]));
  }
  candidatesFor(key) {
    const position = this.filters.indexOf(key);
    return this.products.filter((product) => this.filters.slice(0, position).every((parent) => product.fitment[parent].includes(this.state[parent])));
  }
  populateFilter(key) {
    const trigger = this.querySelector(`[data-ass-filter-trigger="${key}"]`); const options = this.querySelector(`[data-ass-filter-options="${key}"]`); const popover = this.querySelector(`[data-ass-filter-popover="${key}"]`); const values = [...new Set(this.candidatesFor(key).flatMap((product) => product.fitment[key]).filter(Boolean))];
    values.sort((a, b) => key === 'year' ? Number(b) - Number(a) : a.localeCompare(b, undefined, { numeric: true }));
    options.replaceChildren(); options.style.setProperty('--ass-filter-columns', Math.min(values.length || 1, 3)); values.forEach((value) => { const button = document.createElement('button'); button.type = 'button'; button.dataset.assFilterOption = key; button.dataset.value = value; button.setAttribute('role', 'option'); button.setAttribute('aria-selected', String(this.state[key] === value)); const title = document.createElement('strong'); title.textContent = value; button.append(title); options.append(button); });
    popover.querySelector('[data-ass-option-count]').textContent = `${values.length} option${values.length === 1 ? '' : 's'} available.`; trigger.disabled = !this.dataReady || values.length === 0; this.updateFilterTrigger(key);
  }
  matchingProducts() {
    if (!this.filters.every((key) => this.state[key])) return [];
    return this.products.filter((product) => this.filters.every((key) => product.fitment[key].includes(this.state[key])));
  }
  money(cents) {
    if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, this.data.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }
  render() {
    this.dataset.requiresCartSubmission = 'true'; syncSetupDynamicCheckout(this.getAttribute('form-id')); this.panel('filters').hidden = false;
    const skipButton = this.querySelector('[data-ass-skip]'); skipButton.setAttribute('aria-pressed', String(this.state.skipped));
    const complete = !this.state.skipped && this.filters.every((key) => this.state[key]); this.panel('products').hidden = !complete;
    if (complete) this.renderProducts();
    this.panel('variants').hidden = this.state.skipped || !this.state.product;
    if (this.state.product) this.renderVariants();
    this.renderStatus();
  }
  renderProducts() {
    const container = this.querySelector('[data-ass-products]'); const products = this.matchingProducts(); container.replaceChildren();
    if (!products.includes(this.state.product)) { this.state.product = products.find((product) => product.variants.length) || null; this.state.variant = null; this.state.optionValues = []; }
    this.querySelector('[data-ass-match-count]').textContent = `${products.length} compatible Air Suspension product${products.length === 1 ? '' : 's'} found.`;
    products.forEach((product) => {
      const label = document.createElement('label'); label.className = `air-suspension-setup__product${product.variants.length ? '' : ' air-suspension-setup__product--sold-out'}`;
      const input = document.createElement('input'); input.type = 'radio'; input.name = `ass-product-${this.getAttribute('form-id')}`; input.value = product.index; input.checked = this.state.product?.index === product.index; input.disabled = product.variants.length === 0;
      const card = document.createElement('span');
      if (product.image) { const image = document.createElement('img'); setupCardImage(image, product.image); card.append(image); }
      const copy = document.createElement('span'); copy.className = 'air-suspension-setup__product-copy';
      const title = document.createElement('strong'); title.textContent = product.title; copy.append(title);
      const fitment = document.createElement('small'); fitment.textContent = `${this.state.make} ${this.state.model} ${this.state.year} ${this.state.drivetrain}`; copy.append(fitment); card.append(copy);
      const meta = document.createElement('span'); meta.className = 'air-suspension-setup__product-meta';
      const availability = document.createElement('small'); availability.className = `air-suspension-setup__stock${product.variants.length ? '' : ' air-suspension-setup__stock--out'}`;
      if (product.variants.length) { const mark = document.createElement('span'); mark.className = 'air-suspension-setup__stock-mark'; mark.setAttribute('aria-hidden', 'true'); mark.textContent = '✓'; availability.append(mark, document.createTextNode('IN STOCK')); }
      else availability.textContent = 'NO STOCK';
      meta.append(availability); card.append(meta);
      label.append(input, card); container.append(label);
    });
  }
  renderVariants() {
    const container = this.querySelector('[data-ass-variants]'); container.replaceChildren();
    this.state.product.options.forEach((option, optionIndex) => {
      const group = document.createElement('fieldset'); group.className = 'air-suspension-setup__option-group';
      const legend = document.createElement('legend'); legend.textContent = option.name; group.append(legend);
      const values = document.createElement('div'); values.className = 'air-suspension-setup__option-values'; values.style.setProperty('--ass-grid-columns', Math.min(option.values.length || 1, 4));
      option.values.forEach((value) => {
        const label = document.createElement('label'); const input = document.createElement('input'); input.type = 'radio'; input.name = `ass-option-${this.getAttribute('form-id')}-${optionIndex}`; input.value = value; input.dataset.assOptionIndex = optionIndex; input.checked = this.state.optionValues[optionIndex] === value; input.disabled = !this.optionValueAvailable(optionIndex, value);
        const text = document.createElement('span'); const name = document.createElement('strong'); name.textContent = value; const priceValue = this.optionValuePrice(optionIndex, value); const price = document.createElement('small'); price.textContent = priceValue ? this.money(priceValue) : 'Unavailable'; text.append(name, price); label.append(input, text); values.append(label);
      });
      group.append(values); container.append(group);
    });
  }
  optionValueAvailable(optionIndex, value) {
    return this.state.product.variants.some((variant) => variant.options.every((variantValue, index) => index === optionIndex ? variantValue === value : !this.state.optionValues[index] || variantValue === this.state.optionValues[index]));
  }
  optionValuePrice(optionIndex, value) {
    const variants = this.state.product.variants.filter((variant) => variant.options.every((variantValue, index) => index === optionIndex ? variantValue === value : !this.state.optionValues[index] || variantValue === this.state.optionValues[index]));
    return variants.length ? Math.min(...variants.map((variant) => variant.price)) : 0;
  }
  selectedSuspensionVariant() {
    if (!this.state.product || this.state.optionValues.length !== this.state.product.options.length || this.state.optionValues.some((value) => !value)) return null;
    return this.state.product.variants.find((variant) => variant.options.every((value, index) => value === this.state.optionValues[index])) || null;
  }
  renderStatus() {
    const status = this.querySelector('[data-ass-status]');
    this.querySelector('[data-ass-retry]').hidden = !this.loadFailed || this.dataReady || this.state.skipped;
    status.dataset.state = 'info';
    if (this.state.skipped) { status.dataset.state = 'success'; status.textContent = 'No Air Suspension product will be added.'; }
    else if (!this.dataReady) { status.dataset.state = this.loadFailed ? 'error' : 'info'; status.textContent = this.loadFailed ? this.data.loadErrorMessage : this.data.loadingMessage; }
    else if (!this.products.length) { status.dataset.state = 'error'; status.textContent = this.data.emptyMessage; }
    else if (!this.filters.every((key) => this.state[key])) status.textContent = 'Select Make, Model, Year and Drivetrain to find compatible products.';
    else if (!this.matchingProducts().length) { status.dataset.state = 'error'; status.textContent = this.data.emptyMessage; }
    else if (!this.state.product) status.textContent = 'Select a compatible Air Suspension product.';
    else if (!this.state.product.variants.length) { status.dataset.state = 'error'; status.textContent = 'This product is sold out. Please choose another product.'; }
    else if (!this.state.variant) status.textContent = this.state.optionValues.length < this.state.product.options.length || this.state.optionValues.some((value) => !value) ? 'Select every product option to complete this setup.' : 'This option combination is unavailable. Please choose another combination.';
    else { status.dataset.state = 'success'; status.textContent = `${this.state.variant.title} — ${this.money(this.state.variant.price)}`; }
  }
  reportProviderError(message) { const status = this.querySelector('[data-ass-status]'); status.dataset.state = 'error'; status.textContent = message; this.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  getCartItem(bundleId) {
    if (this.state.skipped) return { mainProperties: { 'Air Suspension': 'No suspension needed' } };
    if (!this.dataReady) return { error: this.loadFailed ? this.data.loadErrorMessage : this.data.loadingMessage };
    if (!this.filters.every((key) => this.state[key])) return { error: 'Select your Make, Model, Year, and Drivetrain to find the perfect Air Suspension fit. Already have suspension covered? Choose NO SUSPENSION NEEDED to continue.' };
    if (!this.state.product) return { error: 'Choose a compatible Air Suspension product before adding to cart.' };
    if (!this.state.variant) return { error: 'Choose a complete, available Air Suspension option combination before adding to cart.' };
    const properties = { '_Bundle component': 'Air Suspension', 'Air Suspension System': this.state.product.title, 'Make': this.state.make, 'Model': this.state.model, 'Year': this.state.year, 'Drivetrain': this.state.drivetrain, '_Bundle ID': bundleId };
    this.state.product.options.forEach((option, index) => { properties[option.name] = this.state.optionValues[index]; });
    const mainProperties = { 'Air Suspension - System': this.state.product.title, 'Air Suspension - Make': this.state.make, 'Air Suspension - Model': this.state.model, 'Air Suspension - Year': this.state.year, 'Air Suspension - Drivetrain': this.state.drivetrain };
    this.state.product.options.forEach((option, index) => { mainProperties[`Air Suspension - ${option.name}`] = this.state.optionValues[index]; });
    return { id: this.state.variant.id, quantity: 1, properties, mainProperties };
  }
}
if (!customElements.get('air-suspension-setup')) customElements.define('air-suspension-setup', AirSuspensionSetup);

class FittingSizeSetup extends HTMLElement {
  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.addEventListener('change', () => {
      this.querySelector('[data-fitting-size-message]').hidden = true;
      this.dataset.requiresCartSubmission = 'true';
      syncSetupDynamicCheckout(this.getAttribute('form-id'));
    });
    // Fitting Size is required order information even when no extra fitting
    // product is configured, therefore native dynamic checkout must not bypass it.
    this.dataset.requiresCartSubmission = 'true';
    syncSetupDynamicCheckout(this.getAttribute('form-id'));
  }
  selectedInput() { return this.inputs.find((input) => input.checked); }
  reportProviderError(message) {
    const notice = this.querySelector('[data-fitting-size-message]'); notice.textContent = message; notice.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  getCartItem(bundleId) {
    const selected = this.selectedInput();
    if (!selected) return { error: 'Please select a fitting size.' };
    const fittingSize = selected.value;
    const mainProperties = { 'Fitting Size': fittingSize };
    const variantId = Number(selected.dataset.productVariantId || 0);
    if (!variantId) return { mainProperties };
    return {
      id: variantId,
      quantity: 1,
      properties: { '_Bundle component': 'Fitting Size', 'Fitting Size': fittingSize, '_Bundle ID': bundleId },
      mainProperties
    };
  }
}
if (!customElements.get('fitting-size-setup')) customElements.define('fitting-size-setup', FittingSizeSetup);

class OptionalServicesSetup extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-optional-services-data]').textContent);
    this.options = [];
    this.addEventListener('change', (event) => this.onChange(event));
    this.render();
  }
  money(cents) {
    if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, this.data.moneyFormat);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }
  onChange(event) {
    if (!event.target.matches('[data-optional-service-option]')) return;
    const index = Number(event.target.dataset.optionalServiceOption); this.options[index] = event.target.value; this.options = this.options.slice(0, index + 1); this.render();
  }
  render() {
    const container = this.querySelector('[data-optional-services-options]'); container.replaceChildren();
    if (!this.data.product) { this.dataset.requiresCartSubmission = 'false'; return; }
    this.data.product.options.forEach((option, optionIndex) => {
      const group = document.createElement('fieldset'); group.className = 'optional-services-setup__option-group'; const legend = document.createElement('legend'); legend.textContent = option.name; group.append(legend);
      const values = document.createElement('div'); values.className = 'optional-services-setup__values'; values.style.setProperty('--optional-service-columns', Math.min(option.values.length || 1, 4));
      option.values.forEach((value) => {
        const label = document.createElement('label'); const input = document.createElement('input'); input.type = 'radio'; input.name = `optional-service-${this.getAttribute('form-id')}-${optionIndex}`; input.value = value; input.dataset.optionalServiceOption = optionIndex; input.checked = this.options[optionIndex] === value; input.disabled = !this.optionAvailable(optionIndex, value);
        const box = document.createElement('span'); const name = document.createElement('strong'); name.textContent = value; const price = document.createElement('small'); const priceValue = this.optionPrice(optionIndex, value); price.textContent = priceValue == null ? 'Unavailable' : this.money(priceValue); box.append(name, price); label.append(input, box); values.append(label);
      }); group.append(values); container.append(group);
    });
    const variant = this.selectedVariant(); const status = this.querySelector('[data-optional-services-status]'); status.dataset.state = variant ? 'success' : 'info'; status.textContent = variant ? `${variant.title} — ${this.money(variant.price)}` : 'Select every Optional Services option.';
    this.dataset.requiresCartSubmission = 'true'; syncSetupDynamicCheckout(this.getAttribute('form-id'));
  }
  candidateVariants(index, value) { return this.data.product.variants.filter((variant) => variant.options.every((variantValue, position) => position === index ? variantValue === value : !this.options[position] || variantValue === this.options[position])); }
  optionAvailable(index, value) { return this.candidateVariants(index, value).length > 0; }
  optionPrice(index, value) { const variants = this.candidateVariants(index, value); return variants.length ? Math.min(...variants.map((variant) => variant.price)) : null; }
  selectedVariant() { if (!this.data.product || this.options.length !== this.data.product.options.length || this.options.some((value) => !value)) return null; return this.data.product.variants.find((variant) => variant.options.every((value, index) => value === this.options[index])) || null; }
  reportProviderError(message) { const status = this.querySelector('[data-optional-services-status]'); if (!status) return; status.dataset.state = 'error'; status.textContent = message; this.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  getCartItem(bundleId) {
    if (!this.data.product) return null;
    const variant = this.selectedVariant(); if (!variant) return { error: 'Choose a complete Optional Services option combination.' };
    const selection = this.options.join(' / '); const isNo = this.options.some((value) => /^(no|none|no thanks|not needed)$/i.test(String(value).trim()));
    const mainProperties = { 'Optional Services': `${this.data.product.title} - ${selection}`, 'Optional Services Region': this.data.country };
    if (isNo) return { mainProperties };
    return { id: variant.id, quantity: 1, properties: { '_Bundle component': 'Optional Service', 'Optional Service': this.data.product.title, 'Selected Option': selection, 'Service Region': this.data.country, '_Bundle ID': bundleId }, mainProperties };
  }
}
if (!customElements.get('optional-services-setup')) customElements.define('optional-services-setup', OptionalServicesSetup);

// One authoritative cart checkout route. This capture listener is registered
// before drawers are opened, so focus traps, cart refresh handlers, instant
// navigation and third-party bubble listeners cannot turn checkout into a
// no-op or an in-page jump.
function goToShopifyCheckout(event) {
  const trigger = event.target.closest('[data-direct-checkout]');
  if (!trigger) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (trigger.dataset.checkoutPending === 'true') return;
  trigger.dataset.checkoutPending = 'true';
  trigger.setAttribute('aria-busy', 'true');
  window.location.assign('/checkout');
}
document.addEventListener('click', goToShopifyCheckout, true);
document.addEventListener('submit', (event) => {
  if (event.submitter?.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign('/checkout');
}, true);

// Capture the custom bundle checkout before Shopify's native dynamic checkout
// handlers can submit only the base product or navigate back to the product URL.
document.addEventListener('click', (event) => {
  const checkoutButton = event.target.closest('[data-bundle-checkout]');
  if (!checkoutButton) return;
  const form = checkoutButton.closest('form[is="product-form"]');
  event.preventDefault();
  event.stopImmediatePropagation();
  if (!form) return;
  if (checkoutButton.dataset.bundleCheckoutPending === 'true') return;
  checkoutButton.dataset.bundleCheckoutPending = 'true';
  checkoutButton.setAttribute('disabled', 'disabled');
  checkoutButton.setAttribute('aria-busy', 'true');
  const submitForCheckout = typeof form._onSubmit === 'function'
    ? form._onSubmit(event, true)
    : Promise.reject(new Error('Product checkout form is not ready.'));
  Promise.resolve(submitForCheckout).catch(() => {
    form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      detail: { status: 'error', error: 'Checkout could not start. Please refresh the page and try again.' }
    }));
  }).finally(() => {
    checkoutButton.dataset.bundleCheckoutPending = 'false';
    checkoutButton.removeAttribute('disabled');
    checkoutButton.removeAttribute('aria-busy');
  });
}, true);

function syncSetupDynamicCheckout(formId) {
  const productForm = document.getElementById(formId); if (!productForm) return;
  const dynamicCheckout = productForm.querySelector('.shopify-payment-button'); const bundleCheckout = productForm.querySelector('[data-bundle-checkout]');
  const productSection = productForm.closest('.shopify-section');
  const hasActiveProvider = Array.from(document.querySelectorAll('[data-cart-item-provider]')).some((provider) => provider.dataset.requiresCartSubmission === 'true' && (provider.getAttribute('form-id') === formId || (productSection && provider.closest('.shopify-section') === productSection)));
  if (dynamicCheckout) {
    dynamicCheckout.hidden = hasActiveProvider;
    dynamicCheckout.dataset.bundleDisabled = String(hasActiveProvider);
    dynamicCheckout.style.display = hasActiveProvider ? 'none' : '';
  }
  if (bundleCheckout) bundleCheckout.hidden = !hasActiveProvider;
}

// Re-apply checkout ownership after Shopify replaces product fragments or
// initializes its accelerated-payment element later than the setup blocks.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-cart-item-provider][form-id]').forEach((provider) => syncSetupDynamicCheckout(provider.getAttribute('form-id')));
});
document.addEventListener('variant:changed', (event) => {
  const form = event.target?.closest?.('form[is="product-form"]') || event.target;
  if (form?.id) syncSetupDynamicCheckout(form.id);
});
